<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import {
  Card,
  EmptyState,
  ErrorState,
  IconButton,
  MenuItem,
  MenuPopover,
  NavIcon,
  Skeleton,
  StatusTag,
} from '@taler/ui'
import { formatMonthYear, formatRate } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import LoanRepaidMeter from '@/features/loans/components/LoanRepaidMeter.vue'
import LoanTypeIcon from '@/features/loans/components/LoanTypeIcon.vue'
import type { Loan } from '../internal/types.ts'

defineProps<{
  loans: readonly Loan[]
  isLoading: boolean
  error: Error | null
  retrying?: boolean | undefined
  hasHistory: (loanId: string) => boolean | undefined
  canEdit: boolean
}>()
const emit = defineEmits<{ retry: []; edit: [loan: Loan] }>()

const { locale, t } = useI18n()
const loc = computed(() => locale.value as 'en' | 'hu')
</script>

<template>
  <div>
    <ul v-if="isLoading" aria-hidden="true" class="flex flex-col gap-3 px-3 py-2">
      <li v-for="index in 3" :key="index" class="flex items-center gap-3">
        <Skeleton width="40%" height="1rem" />
      </li>
    </ul>
    <p v-if="isLoading" class="sr-only" role="status">{{ t('loans.loading') }}</p>

    <ErrorState
      v-else-if="error"
      :title="t('loans.errorTitle')"
      :body="t('loans.errorBody')"
      :retry-label="t('loans.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <EmptyState v-else-if="loans.length === 0" :title="t('loans.emptyTitle')" :body="t('loans.emptyBody')">
      <template #icon><NavIcon name="loans" /></template>
    </EmptyState>

    <ul v-else class="flex flex-col gap-[var(--space-cell)]">
      <li v-for="loan in loans" :key="loan.id">
        <Card class="flex min-w-0 items-start gap-[var(--space-cell)]">
          <span
            aria-hidden="true"
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-[color:color-mix(in_srgb,var(--color-accent)_12%,transparent)] text-[color:var(--color-accent)]"
          >
            <LoanTypeIcon :loan-type="loan.loanType" />
          </span>

          <div class="flex min-w-0 flex-1 flex-col gap-[var(--space-cell)]">
            <div class="flex min-w-0 items-center gap-[var(--space-cell)]">
              <RouterLink
                :to="{ name: 'loan-detail', params: { loanId: loan.id } }"
                class="truncate text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)] underline decoration-transparent underline-offset-4 transition-colors duration-[var(--motion-fast)] hover:decoration-current"
              >
                {{ loan.name }}
              </RouterLink>
              <StatusTag v-if="loan.overdue" tone="danger">{{ t('loans.overdue') }}</StatusTag>
            </div>
            <p class="min-w-0 truncate text-[color:var(--color-text)]">
              <MoneyAmount :value="loan.originalPrincipal" size="hero" />
            </p>
            <LoanRepaidMeter :loan="loan" :has-history="hasHistory(loan.id)" />
          </div>

          <dl class="flex shrink-0 gap-[var(--space-group)] text-right">
            <div class="flex flex-col-reverse gap-[var(--space-hair)]">
              <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                {{ t('loans.interestRate') }}
              </dt>
              <dd
                class="text-[length:var(--text-step-4)] font-semibold tabular-nums text-[color:var(--color-text)]"
              >
                {{ formatRate(loan.annualInterestRate, { locale: loc }) }}%
              </dd>
            </div>
            <div class="flex flex-col-reverse gap-[var(--space-hair)]">
              <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                {{ t('loans.endDate') }}
              </dt>
              <dd
                class="text-[length:var(--text-step-4)] font-semibold tabular-nums text-[color:var(--color-text)]"
              >
                {{ loan.endDate === undefined ? t('loans.notSet') : formatMonthYear(loan.endDate, loc) }}
              </dd>
            </div>
          </dl>

          <MenuPopover v-if="canEdit" side="bottom" align="end">
            <template #trigger>
              <IconButton :aria-label="t('loans.actionsLabel', { name: loan.name })">
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
                  <circle cx="8" cy="3.5" r="1.4" />
                  <circle cx="8" cy="8" r="1.4" />
                  <circle cx="8" cy="12.5" r="1.4" />
                </svg>
              </IconButton>
            </template>
            <MenuItem @select="emit('edit', loan)">{{ t('loans.editTerms') }}</MenuItem>
          </MenuPopover>
        </Card>
      </li>
    </ul>
  </div>
</template>
